/**
 * Beyond Bohemian - Trust Badge Starfield
 * Renders a subtle animated starfield with occasional shooting stars
 * behind any element with the class "trust-starfield".
 *
 * Behavior:
 * - Desktop (>749px): twinkling stars + occasional shooting stars
 * - Mobile (<=749px): static stars only (no animation)
 * - prefers-reduced-motion: static stars only
 * - Pauses when section is offscreen (via IntersectionObserver)
 * - DPR-aware canvas sizing for crisp stars on retina displays
 *
 * IMPORTANT: Star positions are stored as fractions (0-1) and generated
 * ONCE on init. They never change on resize. This prevents the "jumping
 * stars" bug on mobile, where scrolling would hide/show the URL bar,
 * fire resize events, and regenerate star positions causing them to
 * teleport across the canvas.
 */
(function () {
  'use strict';

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function isStaticMode() {
    return prefersReducedMotion || window.innerWidth <= 749;
  }

  function initStarfield(section) {
    if (section.dataset.starfieldInit === 'true') return;
    section.dataset.starfieldInit = 'true';

    var canvas = section.querySelector('.trust-starfield__canvas');
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var stars = [];
    var shootingStars = [];
    var frame = 0;
    var lastShootingStar = Date.now();
    var nextShootingStarDelay = Math.random() * 2000 + 3000;
    var width = 0;
    var height = 0;
    var isVisible = true;
    var animationRunning = false;

    // Resize canvas only - never touches the stars array.
    // This is called on init AND on every window resize.
    function sizeCanvas() {
      var rect = section.getBoundingClientRect();
      var dpr = Math.max(1, window.devicePixelRatio || 1);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    // Generate the star field. Called ONCE on init only.
    // Star positions are stored as normalized fractions (0-1) so they
    // stay locked to the same relative spot even if the canvas resizes
    // (e.g. mobile address bar hide/show, orientation change).
    function generateStars() {
      var area = width * height;
      var starCount = Math.min(180, Math.max(60, Math.round(area / 9000)));
      stars = [];
      for (var i = 0; i < starCount; i++) {
        stars.push({
          xPct: Math.random(),
          yPct: Math.random(),
          radius: Math.random() * 1.2 + 0.3,
          alpha: Math.random() * 0.4 + 0.3,
          twinkleSpeed: Math.random() * 0.015 + 0.008,
          twinklePhase: Math.random() * Math.PI * 2
        });
      }
    }

    function createShootingStar() {
      var side = Math.random();
      var startX, startY, angle;
      if (side < 0.5) {
        startX = Math.random() * width * 0.3;
        startY = -50;
        angle = Math.PI / 4 + Math.random() * 0.2;
      } else {
        startX = width - Math.random() * width * 0.3;
        startY = -50;
        angle = (Math.PI * 3) / 4 - Math.random() * 0.2;
      }
      shootingStars.push({
        x: startX,
        y: startY,
        length: Math.random() * 60 + 40,
        speed: Math.random() * 2 + 3,
        angle: angle,
        alpha: Math.random() * 0.3 + 0.5,
        life: 1
      });
    }

    function drawStaticStars() {
      ctx.clearRect(0, 0, width, height);
      for (var i = 0; i < stars.length; i++) {
        var star = stars[i];
        ctx.beginPath();
        ctx.arc(star.xPct * width, star.yPct * height, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, ' + star.alpha + ')';
        ctx.fill();
      }
    }

    function animate() {
      if (isStaticMode()) {
        animationRunning = false;
        drawStaticStars();
        return;
      }

      if (!isVisible) {
        requestAnimationFrame(animate);
        return;
      }

      ctx.clearRect(0, 0, width, height);

      for (var i = 0; i < stars.length; i++) {
        var star = stars[i];
        var twinkle =
          Math.sin(frame * star.twinkleSpeed + star.twinklePhase) * 0.25 + 0.75;
        ctx.beginPath();
        ctx.arc(star.xPct * width, star.yPct * height, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, ' + star.alpha * twinkle + ')';
        ctx.fill();
      }

      var now = Date.now();
      if (now - lastShootingStar > nextShootingStarDelay) {
        createShootingStar();
        lastShootingStar = now;
        nextShootingStarDelay = Math.random() * 2000 + 3000;
      }

      for (var j = shootingStars.length - 1; j >= 0; j--) {
        var s = shootingStars[j];
        s.x += Math.cos(s.angle) * s.speed;
        s.y += Math.sin(s.angle) * s.speed;
        s.life -= 0.008;

        var gradient = ctx.createLinearGradient(
          s.x,
          s.y,
          s.x - Math.cos(s.angle) * s.length,
          s.y - Math.sin(s.angle) * s.length
        );
        gradient.addColorStop(0, 'rgba(200, 180, 255, ' + s.alpha * s.life + ')');
        gradient.addColorStop(0.5, 'rgba(180, 160, 230, ' + s.alpha * s.life * 0.6 + ')');
        gradient.addColorStop(1, 'rgba(200, 180, 255, 0)');

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(
          s.x - Math.cos(s.angle) * s.length,
          s.y - Math.sin(s.angle) * s.length
        );
        ctx.stroke();

        if (
          s.y > height + 100 ||
          s.x > width + 100 ||
          s.x < -100 ||
          s.life <= 0
        ) {
          shootingStars.splice(j, 1);
        }
      }

      frame++;
      requestAnimationFrame(animate);
    }

    function startOrRedraw() {
      if (isStaticMode()) {
        animationRunning = false;
        drawStaticStars();
      } else if (!animationRunning) {
        animationRunning = true;
        requestAnimationFrame(animate);
      }
    }

    sizeCanvas();
    generateStars(); // ONLY called here, never on resize

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        sizeCanvas();          // resize the canvas
        // do NOT regenerate stars - they stay locked to their fractional positions
        startOrRedraw();       // redraw at the same relative positions
      }, 200);
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            isVisible = entries[i].isIntersecting;
          }
        },
        { rootMargin: '100px' }
      );
      io.observe(section);
    }

    startOrRedraw();
  }

  function initAll() {
    var sections = document.querySelectorAll('.trust-starfield');
    for (var i = 0; i < sections.length; i++) {
      initStarfield(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.trust-starfield');
    if (section) initStarfield(section);
  });
})();
